import React, { useState } from "react";
import { motion } from "framer-motion";

/**
 * BreastAnatomyVisual — анатомично точна стилизирана визуализация на
 * напречен разрез на гърдата с автентични тъканни цветове.
 *
 * Цветовете на тъканите следват биологичната им идентичност (Кожа е
 * прасковена, Мастна — жълта, Епител — pink-magenta, Съдове — червени и
 * т.н.). Здравният статус на зоната се показва чрез интензитета на
 * златния (gold) glow и border, без да маскира цвета на тъканта.
 *
 * Структури (от повърхностно към вътрешно):
 *  1. Кожа (Skin)
 *  2. Ареола + Мамилла
 *  3. Мастна тъкан (Adipose)
 *  4. Купърови връзки (Cooper's ligaments)
 *  5. Строма / фиброзна тъкан
 *  6. Лимфни съдове
 *  7. Имунна среда (диспергирани лимфоцити)
 *  8. Кръвоносни съдове
 *  9. Епител: дуктална система + TDLU
 */

// Authentic tissue colours (independent of health status)
const TISSUE_COLOURS = {
  skin:        "#F4D1AE",  // peach
  areola:      "#B45674",  // dusty rose
  nipple:      "#831843",  // deep berry
  adipose:     "#FCD34D",  // yellow
  stroma:      "#A78BFA",  // light purple
  fibrous:     "#7C3AED",  // deeper purple
  ligaments:   "#C7B299",  // tan
  lymphatic:   "#10B981",  // green
  immune:      "#60A5FA",  // light blue
  vascular:    "#DC2626",  // red
  epithelium:  "#EC4899",  // magenta
  tdlu:        "#BE185D",  // deep magenta
};

// Band → glow intensity (gold-tinted, applied via filter)
const BAND_GLOW_RGB = {
  green:  "34, 197, 94",
  yellow: "234, 179, 8",
  orange: "249, 115, 22",
  red:    "239, 68, 68",
};

const LABELS = {
  bg: {
    epithelium: "Епител",
    stroma:     "Строма",
    adipose:    "Мастна",
    immune:     "Имунна",
    vascular:   "Съдова",
    lymphatic:  "Лимфна",
    ligaments:  "Купърови връзки",
    skin:       "Кожа",
    tdlu:       "TDLU",
    hint:       "Преместете курсора — моделът леко следва. Натиснете тъкан за подробности.",
  },
  en: {
    epithelium: "Epithelium",
    stroma:     "Stroma",
    adipose:    "Adipose",
    immune:     "Immune",
    vascular:   "Vascular",
    lymphatic:  "Lymphatic",
    ligaments:  "Cooper's ligaments",
    skin:       "Skin",
    tdlu:       "TDLU",
    hint:       "Move the cursor — the model gently follows. Click a tissue for details.",
  },
  es: {
    epithelium: "Epitelio",
    stroma:     "Estroma",
    adipose:    "Adiposo",
    immune:     "Inmune",
    vascular:   "Vascular",
    lymphatic:  "Linfático",
    ligaments:  "Ligamentos de Cooper",
    skin:       "Piel",
    tdlu:       "TDLU",
    hint:       "Mueva el cursor — el modelo sigue. Toque un tejido para más detalles.",
  },
};

export default function BreastAnatomyVisual({
  biology,
  onSelect,
  selected,
  lang = "bg",
  monthsAhead = 0,  // 0 = now, 6 = 6-month projection
}) {
  const labels = LABELS[lang] || LABELS.bg;
  const [hover, setHover] = useState(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 8;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 8;
    setMouse({ x, y });
  };

  const zoneScore = (zone) => biology?.[zone]?.score ?? 0;
  const zoneBand  = (zone) => biology?.[zone]?.band || "yellow";

  // Compute saturation modifier from band — health affects vibrancy of the tissue colour
  const saturation = (band) =>
    band === "green"  ? 1.0 :
    band === "yellow" ? 0.85 :
    band === "orange" ? 0.7  : 0.55;

  return (
    <div
      onMouseMove={handleMove}
      onMouseLeave={() => setMouse({ x: 0, y: 0 })}
      style={{ perspective: 1400 }}
      data-testid="breast-anatomy-3d"
      className="relative w-full select-none"
    >
      <motion.svg
        viewBox="0 0 640 640"
        className="w-full h-auto"
        style={{ transformStyle: "preserve-3d" }}
        animate={{ rotateY: mouse.x * 0.5, rotateX: -mouse.y * 0.5 }}
        transition={{ type: "spring", stiffness: 60, damping: 18 }}
      >
        <defs>
          {/* Anatomical tissue gradients (per-tissue, NOT band-based) */}
          {Object.entries(TISSUE_COLOURS).map(([key, color]) => (
            <radialGradient key={key} id={`tg-${key}`} cx="50%" cy="50%" r="60%">
              <stop offset="0%"   stopColor={color} stopOpacity="0.9" />
              <stop offset="70%"  stopColor={color} stopOpacity="0.55" />
              <stop offset="100%" stopColor={color} stopOpacity="0.15" />
            </radialGradient>
          ))}

          {/* Health-status glow filters — gold/yellow/orange/red */}
          {Object.entries(BAND_GLOW_RGB).map(([band, rgb]) => (
            <filter key={band} id={`glow-${band}`} x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="6" result="b" />
              <feFlood floodColor={`rgb(${rgb})`} floodOpacity="0.6" />
              <feComposite in2="b" operator="in" result="g" />
              <feMerge>
                <feMergeNode in="g" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          ))}

          {/* Drop shadow */}
          <filter id="dropShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="10" />
            <feOffset dx="0" dy="8" />
            <feComponentTransfer><feFuncA type="linear" slope="0.25" /></feComponentTransfer>
            <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>

          {/* Skin gradient — peach with highlight */}
          <radialGradient id="skinFlesh" cx="50%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#FEE2D6" />
            <stop offset="55%" stopColor="#F4D1AE" />
            <stop offset="100%" stopColor="#C49474" />
          </radialGradient>
        </defs>

        {/* ── Outer skin envelope ───────────────────────────────── */}
        <Zone
          path="M 80 320 a 240 240 0 1 0 480 0 a 240 240 0 1 0 -480 0 Z"
          fill="url(#skinFlesh)"
          opacity={0.96}
          shadow="dropShadow"
          isHover={hover === "skin"}
          isSelected={selected === "skin"}
          band="green"
          onMouseEnter={() => setHover("skin")}
          onMouseLeave={() => setHover(null)}
          onClick={() => onSelect?.("skin")}
          testid="zone-skin"
        />

        {/* ── Adipose ring (subcutaneous fat) ─────────────────────── */}
        <Zone
          path="M 120 320 a 200 200 0 1 0 400 0 a 200 200 0 1 0 -400 0 Z"
          fill={`url(#tg-adipose)`}
          opacity={0.8 * saturation(zoneBand("adipose"))}
          isHover={hover === "adipose"}
          isSelected={selected === "adipose"}
          band={zoneBand("adipose")}
          onMouseEnter={() => setHover("adipose")}
          onMouseLeave={() => setHover(null)}
          onClick={() => onSelect?.("adipose")}
          testid="zone-adipose"
        />

        {/* ── Stroma layer ───────────────────────────────────────── */}
        <Zone
          path="M 170 320 a 150 150 0 1 0 300 0 a 150 150 0 1 0 -300 0 Z"
          fill={`url(#tg-stroma)`}
          opacity={0.85 * saturation(zoneBand("stroma"))}
          isHover={hover === "stroma"}
          isSelected={selected === "stroma"}
          band={zoneBand("stroma")}
          onMouseEnter={() => setHover("stroma")}
          onMouseLeave={() => setHover(null)}
          onClick={() => onSelect?.("stroma")}
          testid="zone-stroma"
        />

        {/* ── Immune speckles (lymphocytes) ───────────────────────── */}
        <motion.g
          onMouseEnter={() => setHover("immune")}
          onMouseLeave={() => setHover(null)}
          onClick={() => onSelect?.("immune")}
          style={{ cursor: "pointer" }}
          animate={{
            filter: hover === "immune" || selected === "immune"
              ? `url(#glow-${zoneBand("immune")})`
              : "none",
          }}
          data-testid="zone-immune"
        >
          {[
            [230, 250],[290, 230],[350, 245],[400, 270],[420, 320],[400, 380],[350, 405],
            [290, 415],[230, 405],[200, 380],[195, 320],[210, 270],[270, 290],[330, 280],
            [390, 330],[360, 370],[280, 380],[240, 340],
          ].map(([cx, cy], i) => (
            <motion.circle
              key={i}
              cx={cx} cy={cy} r="3"
              fill={TISSUE_COLOURS.immune}
              opacity={0.85 * saturation(zoneBand("immune"))}
              animate={{ opacity: [
                0.85 * saturation(zoneBand("immune")),
                0.4 * saturation(zoneBand("immune")),
                0.85 * saturation(zoneBand("immune")),
              ] }}
              transition={{ duration: 3 + (i % 3), repeat: Infinity, delay: i * 0.07 }}
            />
          ))}
        </motion.g>

        {/* ── Cooper's ligaments (fibrous suspensory strands) ─────── */}
        <motion.g
          opacity={0.55}
          onMouseEnter={() => setHover("ligaments")}
          onMouseLeave={() => setHover(null)}
          onClick={() => onSelect?.("ligaments")}
          style={{ cursor: "pointer" }}
          data-testid="zone-ligaments"
        >
          {[
            "M 120 320 Q 200 200 320 220",
            "M 520 320 Q 440 200 320 220",
            "M 120 320 Q 200 440 320 420",
            "M 520 320 Q 440 440 320 420",
            "M 320 80 L 320 200",
            "M 230 130 Q 280 180 320 220",
            "M 410 130 Q 360 180 320 220",
          ].map((d, i) => (
            <path
              key={i}
              d={d}
              stroke={TISSUE_COLOURS.ligaments}
              strokeWidth="1.5"
              fill="none"
              strokeDasharray="6 3"
              opacity="0.7"
            />
          ))}
        </motion.g>

        {/* ── Vascular network ────────────────────────────────────── */}
        <motion.g
          onMouseEnter={() => setHover("vascular")}
          onMouseLeave={() => setHover(null)}
          onClick={() => onSelect?.("vascular")}
          style={{ cursor: "pointer" }}
          animate={{ opacity: hover === "vascular" || selected === "vascular" ? 1 : 0.7 }}
          data-testid="zone-vascular"
        >
          {[
            "M 130 320 Q 200 240 280 270 T 410 250 T 510 320",
            "M 130 320 Q 200 400 280 380 T 410 390 T 510 320",
            "M 250 110 Q 280 200 310 270",
            "M 380 110 Q 350 200 320 270",
            "M 110 250 Q 180 280 240 290",
            "M 530 250 Q 460 280 400 290",
          ].map((d, i) => (
            <path
              key={i}
              d={d}
              stroke={TISSUE_COLOURS.vascular}
              strokeWidth={2.5 * saturation(zoneBand("vascular"))}
              fill="none"
              strokeLinecap="round"
              opacity={0.8 * saturation(zoneBand("vascular"))}
            />
          ))}
        </motion.g>

        {/* ── Lymphatic vessels ───────────────────────────────────── */}
        <motion.g
          onMouseEnter={() => setHover("lymphatic")}
          onMouseLeave={() => setHover(null)}
          onClick={() => onSelect?.("lymphatic")}
          style={{ cursor: "pointer" }}
          data-testid="zone-lymphatic"
        >
          {[
            "M 150 320 Q 220 300 310 330",
            "M 490 320 Q 420 300 330 330",
            "M 320 480 Q 290 420 310 360",
          ].map((d, i) => (
            <path
              key={i}
              d={d}
              stroke={TISSUE_COLOURS.lymphatic}
              strokeWidth="2"
              fill="none"
              strokeDasharray="3 4"
              opacity="0.7"
            />
          ))}
          {[[170, 308],[470, 308],[330, 460]].map(([cx, cy], i) => (
            <circle key={i} cx={cx} cy={cy} r="4" fill={TISSUE_COLOURS.lymphatic} opacity="0.85" />
          ))}
        </motion.g>

        {/* ── Epithelial ductal tree + TDLU (epithelium zone) ─────── */}
        <motion.g
          onMouseEnter={() => setHover("epithelium")}
          onMouseLeave={() => setHover(null)}
          onClick={() => onSelect?.("epithelium")}
          style={{ cursor: "pointer" }}
          animate={{
            filter: hover === "epithelium" || selected === "epithelium"
              ? `url(#glow-${zoneBand("epithelium")})`
              : "none",
          }}
          data-testid="zone-epithelium"
        >
          {/* Main collecting ducts converging at nipple */}
          {[-25, -10, 0, 10, 25].map((deg, i) => {
            const rad = (deg * Math.PI) / 180;
            const x = 320 + Math.sin(rad) * 140;
            const y = 220 + Math.abs(Math.cos(rad)) * 140;
            return (
              <path
                key={i}
                d={`M 320 200 Q ${320 + Math.sin(rad) * 70} ${220 + Math.abs(Math.cos(rad)) * 70} ${x} ${y}`}
                stroke={TISSUE_COLOURS.epithelium}
                strokeWidth="4"
                fill="none"
                strokeLinecap="round"
                opacity={0.85 * saturation(zoneBand("epithelium"))}
              />
            );
          })}

          {/* Branching subsegmental ducts */}
          {[
            "M 250 280 L 215 260", "M 250 280 L 215 305",
            "M 390 280 L 425 260", "M 390 280 L 425 305",
            "M 270 350 L 230 360", "M 270 350 L 250 380",
            "M 370 350 L 410 360", "M 370 350 L 390 380",
          ].map((d, i) => (
            <path key={i} d={d}
              stroke={TISSUE_COLOURS.epithelium} strokeWidth="2.5" fill="none"
              opacity={0.75 * saturation(zoneBand("epithelium"))} strokeLinecap="round" />
          ))}

          {/* TDLU clusters (terminal duct lobular units — KEY anatomy) */}
          {[
            [215, 260],[215, 305],[425, 260],[425, 305],
            [230, 360],[250, 380],[410, 360],[390, 380],
            [275, 405],[365, 405],
          ].map(([cx, cy], i) => (
            <g key={i}>
              {/* Cluster of acini */}
              {[[-6,-4],[6,-4],[-7,4],[7,4],[0,7]].map(([dx, dy], j) => (
                <circle
                  key={j}
                  cx={cx + dx} cy={cy + dy} r="3.2"
                  fill={TISSUE_COLOURS.tdlu}
                  opacity={0.85 * saturation(zoneBand("epithelium"))}
                />
              ))}
            </g>
          ))}
        </motion.g>

        {/* ── Areola + Nipple ─────────────────────────────────────── */}
        <circle cx="320" cy="200" r="36" fill={TISSUE_COLOURS.areola} opacity="0.55" />
        <circle cx="320" cy="200" r="16" fill={TISSUE_COLOURS.nipple} opacity="0.95" />
        <circle cx="320" cy="200" r="6"  fill="#FBCFE8" opacity="0.85" />
        {/* Montgomery tubercles */}
        {[
          [298, 184],[342, 184],[296, 215],[344, 215],[320, 175],[320, 224],
        ].map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="2.2" fill="#7C2D3F" opacity="0.6" />
        ))}

        {/* ── Health badges around the figure ─────────────────────── */}
        <Badge x="80"  y="170" zone="epithelium" label={labels.epithelium} score={zoneScore("epithelium")} band={zoneBand("epithelium")} color={TISSUE_COLOURS.epithelium} onClick={() => onSelect?.("epithelium")} />
        <Badge x="80"  y="320" zone="stroma"     label={labels.stroma}     score={zoneScore("stroma")}     band={zoneBand("stroma")}     color={TISSUE_COLOURS.stroma} onClick={() => onSelect?.("stroma")} />
        <Badge x="80"  y="470" zone="adipose"    label={labels.adipose}    score={zoneScore("adipose")}    band={zoneBand("adipose")}    color={TISSUE_COLOURS.adipose} onClick={() => onSelect?.("adipose")} />
        <Badge x="560" y="170" zone="immune"     label={labels.immune}     score={zoneScore("immune")}     band={zoneBand("immune")}     color={TISSUE_COLOURS.immune} onClick={() => onSelect?.("immune")} />
        <Badge x="560" y="320" zone="vascular"   label={labels.vascular}   score={zoneScore("vascular")}   band={zoneBand("vascular")}   color={TISSUE_COLOURS.vascular} onClick={() => onSelect?.("vascular")} />
        <Badge x="560" y="470" zone="lymphatic"  label={labels.lymphatic}  score={zoneScore("lymphatic")}  band={zoneBand("lymphatic")}  color={TISSUE_COLOURS.lymphatic} onClick={() => onSelect?.("lymphatic")} />

        {/* Anatomy hint label */}
        <text x="320" y="540" textAnchor="middle" style={{ fontSize: 11, fill: TISSUE_COLOURS.tdlu, fontWeight: 700, opacity: 0.7 }}>
          TDLU (Terminal Duct Lobular Unit)
        </text>

        {/* Time-travel indicator */}
        {monthsAhead > 0 && (
          <g transform="translate(320, 100)">
            <rect x="-70" y="-15" width="140" height="30" rx="15" fill="rgba(34, 197, 94, 0.15)" stroke="#22C55E" strokeWidth="1" />
            <text x="0" y="5" textAnchor="middle" style={{ fontSize: 12, fontWeight: 700, fill: "#22C55E" }}>
              {lang === "en" ? `+${monthsAhead} mo projection` : lang === "es" ? `+${monthsAhead} m proyección` : `+${monthsAhead} м прогноза`}
            </text>
          </g>
        )}
      </motion.svg>

      <p className="text-[11px] text-center mt-2 opacity-70">{labels.hint}</p>
    </div>
  );
}


function Zone({ path, fill, opacity, isHover, isSelected, band, onMouseEnter, onMouseLeave, onClick, shadow, testid }) {
  const filter = isSelected || isHover ? `url(#glow-${band})` : shadow ? `url(#${shadow})` : undefined;
  return (
    <motion.path
      d={path}
      fill={fill}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      style={{ cursor: "pointer", filter }}
      animate={{ opacity: isSelected ? Math.min(opacity + 0.15, 1) : isHover ? Math.min(opacity + 0.1, 1) : opacity }}
      transition={{ type: "spring", stiffness: 220, damping: 22 }}
      data-testid={testid}
    />
  );
}


function Badge({ x, y, zone, label, score, band, color, onClick }) {
  const glowColors = { green: "#22C55E", yellow: "#EAB308", orange: "#F97316", red: "#EF4444" };
  const glow = glowColors[band] || "#9CA3AF";
  return (
    <g
      transform={`translate(${x},${y})`}
      onClick={onClick}
      style={{ cursor: "pointer" }}
      data-testid={`badge-${zone}`}
    >
      <motion.rect
        rx="14" width="110" height="56" x="-55" y="-28"
        fill="rgba(255,255,255,0.97)"
        stroke={color}
        strokeWidth="2.5"
        whileHover={{ scale: 1.05 }}
        filter="url(#dropShadow)"
      />
      {/* Tissue colour dot */}
      <circle cx="-42" cy="-12" r="5" fill={color} />
      <text x="-32" y="-7" style={{ fontSize: 10, fontWeight: 600, fill: "#374151" }}>
        {label}
      </text>
      <text x="0" y="18" textAnchor="middle" style={{ fontSize: 18, fontWeight: 800, fill: glow }}>
        {score}
      </text>
    </g>
  );
}
